<template>
  <form @submit.prevent="submitForm" class="form-group">
    <label for="phone">Téléphone</label>
    <input
      id="phone"
      v-model="form.phone"
      type="text"
      placeholder="+237690000000"
      required
    />

    <label for="email">Email <span class="facultatif">(optionel)</span></label>
    <input
      id="email"
      v-model="form.email"
      type="text"
      placeholder="exemple@contact.cm"
    />

    <PasswordField ref="passwordField" :is-registration="true" />

    <div class="btn-group">
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="loading"
        :aria-busy="loading"
      >
        <span v-if="loading" class="btn-loader">
          <span class="spinner" aria-hidden="true"></span>
          Inscription...
        </span>
        <span v-else>S'inscrire</span>
      </button>

      <button
        type="reset"
        class="btn btn-secondary"
        @click="resetForm"
        :disabled="loading"
      >
        Réinitialiser
      </button>
    </div>
  </form>
</template>

<script setup>
import { config } from '@/config/env.js'
import { reactive, ref } from 'vue'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import PasswordField from '@/components/PasswordField.vue'
import { useModalStore } from '@/stores/modalStore'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { useLocaleStore } from '@/stores/localeStore'
import FormFieldsValidation from '@/components/FormFields/FormFieldsValidation.vue'

const toast = useToast()
const passwordField = ref(null)
const modalStore = useModalStore()
const router = useRouter()
const userStore = useUserStore()
const localeStore = useLocaleStore()
const { countryIso } = storeToRefs(localeStore)

const loading = ref(false)

const form = reactive({
  phone: '',
  email: '',
  password: '',
  verifyPassword: ''
})

const resetForm = () => {
  if (loading.value) return

  form.phone = ''
  form.email = ''
  form.password = ''
  form.verifyPassword = ''

  if (passwordField.value?.reset) {
    passwordField.value.reset()
  }
}

const submitForm = async () => {
  if (loading.value) return

  try {
    const passwordData = passwordField.value.getValues()

    if (!passwordData.isValid) {
      toast.error('Les mots de passe ne correspondent pas ou sont invalides.')
      return
    }

    loading.value = true

    const response = await fetch(config.apiUrl + '/user/register', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        phone: form.phone,
        email: form.email,
        password: passwordData.password,
        passwordVerify: passwordData.verifyPassword,
        countryIso: countryIso.value
      })
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      toast.error(data.errors || data.message || "Une erreur est survenue lors de l'inscription.")
      return
    }

    const registeredPhone = form.phone
    const registeredCountryIso = countryIso.value

    resetForm()
    toast.success('Inscription réussie !')

    const { result } = modalStore.openComponent(
      FormFieldsValidation,
      {
        formData: {
          phone: registeredPhone,
          verificationCode: '',
          countryIso: registeredCountryIso
        }
      },
      {
        title: 'Validation de votre compte',
        maxWidth: '500px',
        kind: 'default'
      }
    )

    const res = await result

    if (res?.action === 'submit' && res?.data?.validated) {
      await userStore.fetchUser()
      router.push({ name: 'account' })
    }
  } catch (err) {
    console.error(err)
    toast.error("Une erreur est survenue lors de l'inscription.")
  } finally {
    loading.value = false
  }
}
</script>
